import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Caption1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { Input } from '@ui/components/Input/Input'
import { MinusIcon } from '@ui/icons/Minus.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'

import { useWindowResize } from '@utils/domUtils'

import { Box } from '@shared/components/Box'

import {
  clampQuantity,
  getStepDecimals,
  preventNumericInputChars,
  roundToPrecision,
} from '../../utils/helpers'

const QTY_EPSILON = 0.000001

export interface QuantityValidationError {
  type: 'min' | 'max' | 'step' | 'invalid'
  message: string
}

export interface QuantityInputProps {
  value: number
  onChange: (value: number) => void
  onValidationError?: (error: QuantityValidationError | null) => void
  min?: number | null
  max?: number | null
  step?: number | null
  disabled?: boolean
  label?: string
  rightContent?: React.ReactNode
  errorText?: string
}

export const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  onChange,
  onValidationError,
  min,
  max,
  step,
  disabled = false,
  label,
  rightContent,
  errorText,
}) => {
  const { t } = useTranslation()

  const isMobile = useWindowResize()

  const hasConstraints = typeof min === 'number' && min > 0 && typeof step === 'number' && step > 0
  const effectiveMin = hasConstraints ? min : undefined
  const effectiveMax = typeof max === 'number' && max > 0 ? max : undefined
  const effectiveStep = hasConstraints ? step : undefined
  const decimals = effectiveStep ? getStepDecimals(effectiveStep) : undefined

  const formatValue = useCallback(
    (v: number) => (decimals != null ? v.toFixed(decimals) : String(v)),
    [decimals]
  )

  const [inputValue, setInputValue] = useState<string>(formatValue(value))
  const isTypingRef = useRef(false)
  const hasInteractedRef = useRef(false)

  const validateQuantity = useCallback(
    (num: number): QuantityValidationError | null => {
      if (!Number.isFinite(num)) {
        return { type: 'invalid', message: t('OrderWidget.Invalid quantity') }
      }

      if (effectiveMin != null && num < effectiveMin - QTY_EPSILON) {
        return {
          type: 'min',
          message: t('OrderWidget.The size should be within {{min}} and {{max}}', {
            min: formatValue(effectiveMin),
            max: effectiveMax != null ? formatValue(effectiveMax) : '∞',
          }),
        }
      }

      if (effectiveMax != null && num > effectiveMax + QTY_EPSILON) {
        return {
          type: 'max',
          message: t('OrderWidget.The size should be within {{min}} and {{max}}', {
            min: effectiveMin != null ? formatValue(effectiveMin) : '0',
            max: formatValue(effectiveMax),
          }),
        }
      }

      if (effectiveStep != null && effectiveMin != null) {
        const stepsFromMin = (num - effectiveMin) / effectiveStep
        const isValidStep = Math.abs(stepsFromMin - Math.round(stepsFromMin)) < QTY_EPSILON
        if (!isValidStep) {
          return {
            type: 'step',
            message: t('OrderWidget.Quantity must be in increments of {{step}}', {
              step: formatValue(effectiveStep),
            }),
          }
        }
      }

      return null
    },
    [effectiveMin, effectiveMax, effectiveStep, t, formatValue]
  )

  const currentValidationError = useMemo(() => {
    const num = Number(inputValue)
    if (inputValue === '' || !Number.isFinite(num)) {
      return { type: 'invalid' as const, message: t('OrderWidget.Invalid quantity') }
    }
    return validateQuantity(num)
  }, [inputValue, validateQuantity, t])

  useEffect(() => {
    if (!hasInteractedRef.current) return
    onValidationError?.(currentValidationError)
  }, [currentValidationError, onValidationError])

  useEffect(() => {
    isTypingRef.current = false
    hasInteractedRef.current = false
  }, [min, step])

  useEffect(() => {
    if (isTypingRef.current) return
    setInputValue(formatValue(value))
  }, [value, formatValue])

  const handleMinus = () => {
    hasInteractedRef.current = true
    if (disableAll || !effectiveStep) return
    const next = clampQuantity(
      roundToPrecision(value - effectiveStep, effectiveStep),
      effectiveMin,
      effectiveMax
    )
    isTypingRef.current = false
    setInputValue(formatValue(next))
    onChange(next)
  }

  const handlePlus = () => {
    hasInteractedRef.current = true
    if (disableAll || !effectiveStep) return
    const next = clampQuantity(
      roundToPrecision(value + effectiveStep, effectiveStep),
      effectiveMin,
      effectiveMax
    )
    isTypingRef.current = false
    setInputValue(formatValue(next))
    onChange(next)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    hasInteractedRef.current = true
    const raw = e.currentTarget.value
    setInputValue(raw)
    isTypingRef.current = true

    if (raw === '') return

    const num = Number(raw)
    if (!Number.isFinite(num)) return

    onChange(num)
  }

  const handleBlur = () => {
    hasInteractedRef.current = true
    isTypingRef.current = false

    const num = Number(inputValue)
    if (Number.isFinite(num) && inputValue !== '') {
      setInputValue(formatValue(num))
    }
  }

  const disableAll = disabled || !hasConstraints || (effectiveMax != null && effectiveMax <= 0)
  const disablePlus = disableAll || (effectiveMax != null && value >= effectiveMax - QTY_EPSILON)
  const disableMinus = disableAll || (effectiveMin != null && value <= effectiveMin + QTY_EPSILON)

  const validationErrorMessage = currentValidationError?.message
  const displayError =
    errorText != null ? errorText : hasInteractedRef.current ? validationErrorMessage : undefined

  return (
    <Box flex direction='column' gap='x4'>
      <Box flex direction='row' align='center' gap='x12'>
        <Box flex direction='row' align='center' fullWidth>
          <IconButton
            variant='secondary'
            onClick={handleMinus}
            disabled={disableMinus}
            icon={MinusIcon}
            size={isMobile ? 'sm' : 'md'}
          />
          <Box px='x8' fullWidth>
            <Input
              title={label ?? t('Portfolio.Lots')}
              value={inputValue}
              fullWidth
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={preventNumericInputChars}
              type='number'
              inputMode='decimal'
              step={effectiveStep ?? undefined}
              min={effectiveMin ?? undefined}
              max={effectiveMax ?? undefined}
              decimalPlaces={decimals}
              disabled={disableAll}
              size='lg'
              label={label ?? ''}
              error={!!displayError}
            />
          </Box>
          <IconButton
            variant='secondary'
            onClick={handlePlus}
            disabled={disablePlus}
            icon={PlusIcon}
            size={isMobile ? 'sm' : 'md'}
          />
        </Box>
        {rightContent}
      </Box>
      {displayError && (
        <Box mt='x4'>
          <Caption1 color='text-danger-high-em'>{displayError}</Caption1>
        </Box>
      )}
    </Box>
  )
}
